import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import type { ContactDetail, ContactGroupSummary, UpdateContactRequest } from "@multi-chat/shared";
import { GitMerge, Layers, Pencil, Phone, Tag, User } from "lucide-react";
import { Button } from "../../../components/ui/button/index.js";
import {
  AlertDialog,
  AlertDialogActions,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogTitle,
} from "../../../components/ui/alert-dialog/index.js";
import { ChannelIcon } from "../../../components/channel/ChannelIcon.js";
import { CardSkeleton } from "../../../components/common/WorkspaceLoader.js";
import { channelBadgeClassName } from "../../../components/channel/channel-presentation.js";
import { ContactBirthdayField, formatContactBirthday } from "../ContactBirthdayField.js";
import { ContactFormField, ContactFormSection, ContactViewField, ContactViewSection } from "../ContactFormField.js";
import { ContactGroupPreview } from "../ContactGroupPreview.js";
import { ContactGroupMultiSelect } from "../ContactGroupMultiSelect/index.js";
import { contactInitials } from "../contact-display-name.js";
import {
  contactGroupSelectionToUpdateRequest,
  emptyContactGroupSelection,
  type ContactGroupSelectionValue,
} from "../contact-group-selection-value.js";
import { InternationalPhoneField } from "../../phone/InternationalPhoneField.js";
import { mapContactError } from "../contact-errors.js";
import { LtrValue } from "../../../components/common/LtrValue.js";
import { MergeContactsDialog } from "./MergeContactsDialog.js";
import { cn } from "../../../lib/utils.js";
import {
  contactFormTwoColClass,
  contactIdentityAvatarClass,
  contactIdentityHeaderClass,
  contactSheetBodyClass,
  contactSheetFooterClass,
} from "../contact-sheet-presentation.js";

export type ContactDetailMode = "view" | "edit";

export function ContactDetailEditor({
  detail,
  loading,
  groups,
  organizationId,
  mode,
  onModeChange,
  onSave,
  onDelete,
  onRestore,
  onMerged,
}: {
  detail: ContactDetail | null;
  loading: boolean;
  groups: ContactGroupSummary[];
  organizationId: number;
  mode: ContactDetailMode;
  onModeChange: (mode: ContactDetailMode) => void;
  onSave: (body: UpdateContactRequest) => Promise<void>;
  onDelete: () => Promise<void>;
  onRestore: () => Promise<void>;
  onMerged?: () => void;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [phoneCountryIso, setPhoneCountryIso] = useState("IL");
  const [birthday, setBirthday] = useState("");
  const [groupSelection, setGroupSelection] = useState<ContactGroupSelectionValue>(
    emptyContactGroupSelection(),
  );
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const locale = i18n.language.startsWith("he") ? "he-IL" : "en-US";

  const syncFromDetail = (next: ContactDetail) => {
    setFirstName(next.firstName ?? "");
    setLastName(next.lastName ?? "");
    setDisplayName(next.displayName);
    setEmail(next.email ?? "");
    setPhone(next.normalizedPhoneNumber ?? "");
    setPhoneCountryIso("IL");
    setBirthday(next.birthday ?? "");
    setGroupSelection({
      groupIds: next.groups.map((g) => g.id),
      newGroupNames: [],
    });
    setError(null);
  };

  useEffect(() => {
    if (!detail) return;
    syncFromDetail(detail);
  }, [detail]);

  const formatDate = (iso: string) =>
    new Intl.DateTimeFormat(locale, {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(iso));

  const handleCancelEdit = () => {
    if (detail) syncFromDetail(detail);
    setError(null);
    onModeChange("view");
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await onSave({
        firstName,
        lastName,
        displayName,
        email: email || null,
        phone: phone || null,
        phoneCountryIso,
        birthday: birthday || null,
        ...contactGroupSelectionToUpdateRequest(groupSelection),
      });
      onModeChange("view");
    } catch (err) {
      setError(mapContactError(t, err, t("contacts.detail.saveError")));
    } finally {
      setSaving(false);
    }
  };

  if (loading || !detail) {
    return (
      <div className={contactSheetBodyClass}>
        <CardSkeleton label={t("contacts.loading")} testId="contact-detail-loading" />
      </div>
    );
  }

  const phoneLocked = detail.whatsAppPhoneLocked;
  const empty = t("contacts.detail.emptyValue");
  const firstOpenConversation = detail.platformProfiles.find((p) => p.conversationId)?.conversationId;
  const deleted = Boolean(detail.deletedAt);

  return (
    <>
      <div
        className={cn(contactSheetBodyClass, "grid gap-0 content-start")}
        data-testid={`contact-detail-mode-${mode}`}
      >
        {deleted ? (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[0.8125rem] text-amber-900">
            {t("contacts.detail.deleted")}
            <Button className="mt-2" size="sm" variant="outline" onClick={() => void onRestore()}>
              {t("contacts.detail.restore")}
            </Button>
          </div>
        ) : null}

        {mode === "view" ? (
          <>
            <div className={contactIdentityHeaderClass} data-testid="contact-detail-view-header">
              <div className="flex min-w-0 items-start gap-2.5">
                <span className={contactIdentityAvatarClass} aria-hidden="true">
                  {contactInitials(detail.displayName, detail.firstName, detail.lastName)}
                </span>
                <div className="min-w-0 grid gap-0.5">
                  <h3 className="m-0 truncate text-[1.0625rem] font-semibold tracking-[-0.01em] text-foreground">
                    {detail.displayName}
                  </h3>
                  <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-0.5 text-[0.75rem] text-muted">
                    {detail.normalizedPhoneNumber ? (
                      <LtrValue title={detail.normalizedPhoneNumber}>{detail.normalizedPhoneNumber}</LtrValue>
                    ) : null}
                    {detail.normalizedPhoneNumber && detail.email ? (
                      <span aria-hidden="true">·</span>
                    ) : null}
                    {detail.email ? (
                      <LtrValue title={detail.email}>{detail.email}</LtrValue>
                    ) : null}
                  </div>
                </div>
              </div>
              {!deleted ? (
                <div className="flex shrink-0 flex-wrap items-center gap-1.5">
                  {firstOpenConversation ? (
                    <Button asChild size="sm" variant="ghost" className="h-8 px-2.5 text-[0.75rem]">
                      <Link to={`/inbox?conversation=${firstOpenConversation}`}>
                        {t("contacts.detail.openConversation")}
                      </Link>
                    </Button>
                  ) : null}
                  <Button
                    type="button"
                    size="sm"
                    className="h-8 px-2.5"
                    onClick={() => onModeChange("edit")}
                    data-testid="contact-detail-edit"
                  >
                    <Pencil size={13} aria-hidden="true" />
                    {t("contacts.detail.edit")}
                  </Button>
                </div>
              ) : null}
            </div>

            <ContactViewSection
              legend={t("contacts.create.sections.basicDetails")}
              icon={<User className="size-3" />}
            >
              <dl className="grid grid-cols-1 gap-x-4 gap-y-2 sm:grid-cols-2">
                <ContactViewField label={t("contacts.columns.firstName")}>
                  {detail.firstName || empty}
                </ContactViewField>
                <ContactViewField label={t("contacts.columns.lastName")}>
                  {detail.lastName || empty}
                </ContactViewField>
                <ContactViewField label={t("contacts.columns.displayName")}>
                  {detail.displayName || empty}
                </ContactViewField>
                <ContactViewField label={t("contacts.birthday.label")}>
                  <span data-testid="contact-detail-birthday">
                    {formatContactBirthday(detail.birthday, locale) || empty}
                  </span>
                </ContactViewField>
              </dl>
            </ContactViewSection>

            <ContactViewSection
              legend={t("contacts.create.sections.communication")}
              icon={<Phone className="size-3" />}
            >
              <dl className="grid grid-cols-1 gap-x-4 gap-y-2 sm:grid-cols-2">
                <ContactViewField label={t("contacts.columns.phone")}>
                  {detail.normalizedPhoneNumber ? (
                    <LtrValue title={detail.normalizedPhoneNumber}>{detail.normalizedPhoneNumber}</LtrValue>
                  ) : (
                    empty
                  )}
                </ContactViewField>
                <ContactViewField label={t("contacts.columns.email")}>
                  {detail.email ? <LtrValue title={detail.email}>{detail.email}</LtrValue> : empty}
                </ContactViewField>
              </dl>
            </ContactViewSection>

            <ContactViewSection
              legend={t("contacts.columns.groups")}
              icon={<Tag className="size-3" />}
            >
              <ContactGroupPreview
                groups={detail.groups}
                empty={<p className="m-0 text-[0.8125rem] text-muted">{empty}</p>}
                testId="contact-detail-groups"
              />
            </ContactViewSection>

            {detail.platformProfiles.length > 0 ? (
              <ContactViewSection
                legend={t("contacts.detail.platformProfiles")}
                icon={<Layers className="size-3" />}
              >
                <ul className="m-0 grid list-none gap-1.5 p-0">
                  {detail.platformProfiles.map((p) => {
                    const disconnected = p.platformConnectionStatus === "DISCONNECTED";
                    return (
                      <li
                        key={p.id}
                        className={cn(
                          "flex flex-wrap items-center gap-x-2 gap-y-1 text-[0.8125rem]",
                          disconnected && "opacity-60",
                        )}
                      >
                        <span
                          className={cn(
                            "inline-grid size-5 place-items-center rounded-full",
                            channelBadgeClassName(p.platformType),
                          )}
                        >
                          <ChannelIcon platformType={p.platformType} size={9} className="text-white" />
                        </span>
                        <span className="min-w-0 font-medium">
                          {p.platformDisplayName ?? p.platformUsername ?? p.externalProfileId}
                        </span>
                        {disconnected ? (
                          <span className="text-[0.6875rem] font-normal text-muted">
                            (
                            {t(
                              `contacts.detail.connectionStatus.${p.platformConnectionStatus.toLowerCase()}`,
                            )}
                            )
                          </span>
                        ) : null}
                        {p.normalizedPhoneNumber ? (
                          <LtrValue className="text-muted" title={p.normalizedPhoneNumber}>
                            {p.normalizedPhoneNumber}
                          </LtrValue>
                        ) : null}
                        {p.conversationId ? (
                          <Link
                            to={`/inbox?conversation=${p.conversationId}`}
                            className="text-[0.75rem] text-accent hover:underline"
                          >
                            {t("contacts.detail.openConversation")}
                          </Link>
                        ) : null}
                      </li>
                    );
                  })}
                </ul>
              </ContactViewSection>
            ) : null}

            <div className="border-t border-border/40 pt-2.5 text-[0.6875rem] leading-relaxed text-muted">
              <div>
                {t("contacts.columns.createdAt")}:{" "}
                <LtrValue className="tabular-nums">{formatDate(detail.createdAt)}</LtrValue>
              </div>
              <div>
                {t("contacts.columns.updatedAt")}:{" "}
                <LtrValue className="tabular-nums">{formatDate(detail.updatedAt)}</LtrValue>
              </div>
            </div>
          </>
        ) : (
          <div className="grid gap-4 content-start pt-1" data-testid="contact-detail-edit-form">
            <ContactFormSection legend={t("contacts.create.sections.basicDetails")}>
              <div className={contactFormTwoColClass}>
                <ContactFormField
                  id="contact-edit-first-name"
                  label={t("contacts.columns.firstName")}
                  placeholder={t("contacts.columns.firstName")}
                  value={firstName}
                  disabled={deleted || saving}
                  onChange={setFirstName}
                />
                <ContactFormField
                  id="contact-edit-last-name"
                  label={t("contacts.columns.lastName")}
                  placeholder={t("contacts.columns.lastName")}
                  value={lastName}
                  disabled={deleted || saving}
                  onChange={setLastName}
                />
                <div className="sm:col-span-2">
                  <ContactFormField
                    id="contact-edit-display-name"
                    label={t("contacts.columns.displayName")}
                    placeholder={t("contacts.columns.displayName")}
                    value={displayName}
                    disabled={deleted || saving}
                    onChange={setDisplayName}
                  />
                </div>
                <div className="sm:col-span-2">
                  <ContactBirthdayField
                    id="contact-edit-birthday"
                    value={birthday}
                    disabled={deleted || saving}
                    onChange={setBirthday}
                  />
                </div>
              </div>
            </ContactFormSection>

            <ContactFormSection legend={t("contacts.create.sections.communication")}>
              <div className="grid gap-2.5">
                <ContactFormField
                  id="contact-edit-email"
                  label={t("contacts.columns.email")}
                  type="email"
                  placeholder={t("contacts.columns.email")}
                  value={email}
                  disabled={deleted || saving}
                  onChange={setEmail}
                />
                <div className="min-w-0" data-testid="contact-edit-phone-field">
                  <InternationalPhoneField
                    id="contact-edit-phone"
                    value={phone}
                    defaultCountryIso={phoneCountryIso}
                    onChange={setPhone}
                    onCountryIsoChange={setPhoneCountryIso}
                    disabled={deleted || phoneLocked || saving}
                    readOnly={phoneLocked}
                    showLockIndicator={phoneLocked}
                  />
                  {phoneLocked ? (
                    <p className="mt-1 text-[0.75rem] text-muted">{t("contacts.detail.phoneLocked")}</p>
                  ) : null}
                </div>
              </div>
            </ContactFormSection>

            <ContactFormSection legend={t("contacts.groups.multiSelect.label")}>
              <ContactGroupMultiSelect
                groups={groups}
                value={groupSelection}
                onChange={setGroupSelection}
                disabled={deleted || saving}
                presentation="section"
              />
            </ContactFormSection>

            {detail.platformProfiles.length > 0 ? (
              <ContactViewSection legend={t("contacts.detail.platformProfiles")}>
                <ul className="m-0 grid list-none gap-1.5 p-0">
                  {detail.platformProfiles.map((p) => {
                    const disconnected = p.platformConnectionStatus === "DISCONNECTED";
                    return (
                      <li
                        key={p.id}
                        className={cn(
                          "flex flex-wrap items-center gap-x-2 gap-y-1 text-[0.8125rem]",
                          disconnected && "opacity-60",
                        )}
                      >
                        <span
                          className={cn(
                            "inline-grid size-5 place-items-center rounded-full",
                            channelBadgeClassName(p.platformType),
                          )}
                        >
                          <ChannelIcon platformType={p.platformType} size={9} className="text-white" />
                        </span>
                        <span className="min-w-0 font-medium">
                          {p.platformDisplayName ?? p.platformUsername ?? p.externalProfileId}
                        </span>
                        {p.conversationId ? (
                          <Link
                            to={`/inbox?conversation=${p.conversationId}`}
                            className="text-[0.75rem] text-accent hover:underline"
                          >
                            {t("contacts.detail.openConversation")}
                          </Link>
                        ) : null}
                      </li>
                    );
                  })}
                </ul>
              </ContactViewSection>
            ) : null}

            <div className="text-[0.6875rem] leading-relaxed text-muted">
              <div>
                {t("contacts.columns.createdAt")}: {formatDate(detail.createdAt)}
              </div>
              <div>
                {t("contacts.columns.updatedAt")}: {formatDate(detail.updatedAt)}
              </div>
            </div>
          </div>
        )}
      </div>

      {!deleted ? (
        <div className={contactSheetFooterClass}>
          {error ? (
            <p className="mb-3 rounded-lg border border-red-200/80 bg-red-50 px-3 py-2 text-[0.8125rem] text-red-700">
              {error}
            </p>
          ) : null}
          {mode === "view" ? (
            <div className="flex flex-wrap gap-2">
              <Button variant="ghost" size="sm" className="h-8" onClick={() => setMergeOpen(true)}>
                <GitMerge size={14} aria-hidden="true" />
                {t("contacts.merge.action")}
              </Button>
              <Button variant="ghost" size="sm" className="h-8 text-red-700 hover:text-red-800" onClick={() => setConfirmDelete(true)}>
                {t("contacts.detail.delete")}
              </Button>
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              <Button className="flex-1" onClick={() => void handleSave()} disabled={saving}>
                {t("common.save")}
              </Button>
              <Button variant="outline" onClick={handleCancelEdit} disabled={saving}>
                {t("common.cancel")}
              </Button>
            </div>
          )}
        </div>
      ) : null}

      <MergeContactsDialog
        open={mergeOpen}
        organizationId={organizationId}
        primaryContactId={Number(detail.id)}
        onClose={() => setMergeOpen(false)}
        onMerged={() => {
          setMergeOpen(false);
          onMerged?.();
        }}
      />

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogTitle>{t("contacts.detail.deleteConfirmTitle")}</AlertDialogTitle>
          <AlertDialogDescription>{t("contacts.detail.deleteConfirmBody")}</AlertDialogDescription>
          <AlertDialogActions>
            <Button variant="outline" onClick={() => setConfirmDelete(false)}>
              {t("common.cancel")}
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                void onDelete();
                setConfirmDelete(false);
              }}
            >
              {t("contacts.detail.delete")}
            </Button>
          </AlertDialogActions>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
